import { connectAuthEmulator, getAuth, TotpSecret, User } from "firebase/auth";
import s200 from "src/Consts/responses/s200";
import s400 from "src/Consts/responses/s400";
import { initFirebase } from ".";
import { env, general } from "../../Consts/config";
import { Response, UserStatus } from "@version2/domain-package";
import { updateUserDoc } from "./firestore/user_doc";
import { updateNotificationsDoc } from "./firestore/notifications_doc";
import { accountBlockedModal } from "src/Components/Modals/AccountBlocked";
import type { MessageInstance, HookAPI } from "@version2/ui-package";
import * as authPackage from "@version2/auth-package";

initFirebase();

const auth = getAuth();
if (env.IS_LOCAL) {
	connectAuthEmulator(auth, `http://localhost:${env.AUTH_EMULATOR_PORT}`);
}
const { createFirebaseAuthService } = authPackage;

const authService = createFirebaseAuthService<Response>({
	auth,
	projectName: general.PROJECT_NAME,
	parseError: Response.fromJson,
	successResponse: s200.GENERAL__SUCCESS,
	userNotFoundResponse: s400.USER__NOT_FOUND,
	onPostSignUp: async ({ username, subscribe }) => {
		await updateUserDoc(username);
		await updateNotificationsDoc({
			newsletter: subscribe,
			important_updates: true,
		});
	},
});

type L10nTranslator = (key: string) => string;

const parseUserStatusFromBlockingResponse = (
	authError: { message?: string },
	modal: HookAPI
): boolean => {
	const blockingFunctionMessage =
		"Firebase: HTTP Cloud Function returned an error: ";
	if (!authError.message?.startsWith(blockingFunctionMessage)) {
		return false;
	}

	let userStatus: UserStatus | undefined;
	let messageString: string | undefined;
	try {
		let messageStr = authError.message.replace(blockingFunctionMessage, "");
		messageStr = messageStr.replace(" (auth/internal-error).", "");
		const jsonMessage = JSON.parse(messageStr);
		const error = Response.fromJson(jsonMessage?.error?.details);
		userStatus = error.data?.user_status;
		messageString = error.data?.message;
	} catch {
		return false;
	}

	if (!userStatus) {
		return false;
	}

	accountBlockedModal({
		modal,
		userStatus,
		message: messageString,
	});
	return true;
};

const {
	getCurrentUser,
	signOutUser,
	signUpWithEmail,
	isValidUser,
	listenToAuthState,
	sendUserPasswordResetEmail,
	resetPassword,
	changePassword,
	requestSendEmailVerification,
	verifyEmail,
	revert2fa,
	generateMultiFactorQrCode,
	enrollMultiFactor,
	unEnrollMultiFactor,
	reAuthWithPassword,
	checkIfMultiFactorAuthIsEnrolled,
	resolveTOTPCode,
} = authService;

const generateMultiFactorSecret = async (): Promise<TotpSecret | null> =>
	(authService.generateMultiFactorSecret() as Promise<TotpSecret | null>);

const signInWithEmail = (
	message: MessageInstance,
	modal: HookAPI,
	email: string,
	password: string,
	remember: boolean,
	l10n: L10nTranslator
): Promise<User | undefined> =>
	authService.signInWithEmail(message, email, password, remember, l10n, {
		onBlockingInternalError: (authError) =>
			parseUserStatusFromBlockingResponse(authError, modal),
	});

const changeEmail = async (emailAddress: string) => {
	const authApi = await import("../api/auth");
	return authApi.changeEmail(emailAddress);
};

export {
	authService,
	getCurrentUser,
	signOutUser,
	signInWithEmail,
	signUpWithEmail,
	isValidUser,
	listenToAuthState,
	sendUserPasswordResetEmail,
	resetPassword,
	changePassword,
	requestSendEmailVerification,
	verifyEmail,
	generateMultiFactorSecret,
	generateMultiFactorQrCode,
	enrollMultiFactor,
	unEnrollMultiFactor,
	reAuthWithPassword,
	checkIfMultiFactorAuthIsEnrolled,
	resolveTOTPCode,
	revert2fa,
	changeEmail,
};
